/**
 * Keeps the canvas store's React Flow nodes in line with the frames store.
 *
 * - `node.data` is always the frame object in `framesById`, never a copy.
 *   Only this module sets it: the subscription for frame changes, and the
 *   link applied to every `nodes` write (a stale node array can neither
 *   bring back old data nor resurrect a removed frame).
 * - A node is created only for a frame placed by `lib/frame-ingest.ts`
 *   through the one-shot placement handoff; positions then live in `nodes`.
 * - Data changes and removals are applied with mutation emit suppressed:
 *   every window derives them from its own frames store. Node additions and
 *   loads are emitted, because they carry geometry a mirror cannot derive.
 */
import type { Node } from "@xyflow/react";
import type { StateCreator, StoreApi } from "zustand";
import { getWindowRoleSync } from "@/desktop/use-window-role";
import type { FrameData } from "@/lib/frame-data";
import { canvasFocusPresentation } from "@/lib/focus/focus-renderer-kind";
import { withMutationEmitSuppressed } from "@/lib/mirrored-store";
import { useFramesStore, type FramesState } from "@/stores/frames-store";
import type { CanvasState } from "@/stores/canvas-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useSelectionStore } from "@/stores/selection-store";
import {
  frameNodeFromPlacement,
  frameNodeHidden,
  nodeFollowingFrame,
  type FramePlacement,
} from "./canvas-frame-geometry";
import { loadedFrameNodesState } from "./canvas-frame-load";
import { removeFrameNodes } from "./canvas-frame-removal";

type FrameNode = Node<FrameData>;

interface PlacementHandoff {
  placements: ReadonlyMap<string, FramePlacement>;
  load: boolean;
}

let handoff: PlacementHandoff | null = null;

/**
 * Run a frames-store write whose new frames get canvas nodes at these
 * placements. `load` rebuilds every node (a full frame list). The handoff is
 * read once, by the subscription the write triggers, and never again.
 */
export function placeFrameNodes(
  placements: ReadonlyMap<string, FramePlacement>,
  options: { load: boolean },
  write: () => void,
): void {
  handoff = { placements, load: options.load };
  try {
    write();
  } finally {
    handoff = null;
  }
}

function takeHandoff(): PlacementHandoff | null {
  const taken = handoff;
  handoff = null;
  return taken;
}

let keepsUnarrivedNodes = false;

/**
 * Apply an owner's canvas patch on a mirror. The owner emits a new node
 * before the frames patch that carries its frame, so such a node keeps its
 * incoming data until the frame lands and the subscription links it.
 */
export function withUnarrivedFrameNodesKept<T>(fn: () => T): T {
  const previous = keepsUnarrivedNodes;
  keepsUnarrivedNodes = getWindowRoleSync() === "mirror";
  try {
    return fn();
  } finally {
    keepsUnarrivedNodes = previous;
  }
}

/** Link `data` to the frames store; drop nodes whose frame is gone. */
function linkFrameNodes(
  nodes: FrameNode[],
  focusHiddenFrameIds: ReadonlySet<string>,
): FrameNode[] {
  const { framesById } = useFramesStore.getState();
  let changed = false;
  const linked: FrameNode[] = [];
  for (const node of nodes) {
    const frame = framesById[node.id];
    if (!frame) {
      changed = true;
      if (keepsUnarrivedNodes && node.data) linked.push(node);
      continue;
    }
    const next = nodeFollowingFrame(node, frame, focusHiddenFrameIds);
    if (next !== node) changed = true;
    linked.push(next);
  }
  return changed ? linked : nodes;
}

function linkedPartial(
  state: CanvasState,
  partial: Partial<CanvasState> | CanvasState | null,
): Partial<CanvasState> | CanvasState | null {
  if (!partial || partial === state || !partial.nodes) return partial;
  if (partial.nodes === state.nodes) return partial;
  return {
    ...partial,
    nodes: linkFrameNodes(
      partial.nodes,
      partial.focusHiddenFrameIds ?? state.focusHiddenFrameIds,
    ),
  };
}

/**
 * Store middleware, inside `mirroredStore` so mirrors receive the linked
 * nodes: every `nodes` write is linked to the frames store. A plain partial
 * stays a plain partial (the mirror relay treats the two differently).
 */
export function linkedFrameNodes(
  initializer: StateCreator<CanvasState, [], []>,
): StateCreator<CanvasState, [], []> {
  return (set, get, api) => {
    const write = set as (partial: unknown, replace?: boolean) => void;
    const linkedSet = ((partial: unknown, replace?: boolean) =>
      typeof partial === "function"
        ? write(
            (state: CanvasState) =>
              linkedPartial(
                state,
                (partial as (s: CanvasState) => Partial<CanvasState>)(state),
              ),
            replace,
          )
        : write(
            linkedPartial(get(), partial as Partial<CanvasState> | null),
            replace,
          )) as typeof set;
    api.setState = linkedSet as typeof api.setState;
    return initializer(linkedSet, get, api);
  };
}

function relinkedNodesState(
  state: CanvasState,
  framesById: FramesState["framesById"],
): Partial<CanvasState> | CanvasState {
  let changed = false;
  const nodes = state.nodes.map((node) => {
    const frame = framesById[node.id];
    if (!frame) return node;
    const next = nodeFollowingFrame(node, frame, state.focusHiddenFrameIds);
    if (next !== node) changed = true;
    return next;
  });
  return changed ? { nodes } : state;
}

/** New nodes go on top, as they always did; only a full load rebuilds order. */
function placedNodesState(
  state: CanvasState,
  frames: FramesState,
  placements: ReadonlyMap<string, FramePlacement>,
): Partial<CanvasState> | CanvasState {
  const existing = new Set(state.nodes.map((node) => node.id));
  const focus = canvasFocusPresentation(
    usePresentationStore.getState().presentation,
  );
  let focusHiddenFrameIds = state.focusHiddenFrameIds;
  let nodes: FrameNode[] | null = null;
  for (const [id, placement] of placements) {
    const frame = frames.framesById[id];
    if (!frame || existing.has(id)) continue;
    if (focus && !focus.frameIds.includes(id)) {
      focusHiddenFrameIds = new Set([...focusHiddenFrameIds, id]);
    }
    const node = frameNodeFromPlacement(frame, placement);
    nodes ??= [...state.nodes];
    nodes.push({
      ...node,
      hidden: frameNodeHidden(frame, focusHiddenFrameIds),
    });
  }
  return nodes ? { nodes, focusHiddenFrameIds } : state;
}

function removedFrameIds(next: FramesState, prev: FramesState): string[] {
  const removed: string[] = [];
  for (const id of prev.frameOrder) {
    if (!next.framesById[id]) removed.push(id);
  }
  return removed;
}

function followFrames(
  store: StoreApi<CanvasState>,
  next: FramesState,
  prev: FramesState,
): void {
  const placed = takeHandoff();
  if (placed?.load) {
    const loaded = loadedFrameNodesState(
      store.getState(),
      next,
      placed.placements,
    );
    // The ended presentation lands first, so a mirror's focus guard never
    // keeps its nodes over the loaded ones.
    if (loaded.endsPresentation) {
      usePresentationStore.getState().setPresentation(null);
    }
    useSelectionStore.getState().retain((id) => loaded.nodeIds.has(id));
    store.setState(loaded.canvas);
    return;
  }
  if (next.framesById === prev.framesById) return;
  withMutationEmitSuppressed(() => {
    const removed = removedFrameIds(next, prev);
    if (removed.length > 0) removeFrameNodes(store, removed);
    store.setState((state) => relinkedNodesState(state, next.framesById));
  });
  if (placed && placed.placements.size > 0) {
    store.setState((state) => placedNodesState(state, next, placed.placements));
  }
}

/** Subscribe the canvas store to the frames store. Called once by the canvas store. */
export function followFramesStore(store: StoreApi<CanvasState>): void {
  useFramesStore.subscribe((next, prev) => followFrames(store, next, prev));
}
